import { computed, type ComputedRef } from "vue";
import type {
  CuiBillingFieldsLabels,
  CuiBrandingFieldsLabels,
  CuiBrandingLogosLabels,
  CuiLocaleSelectLabels,
  CuiWorkspaceLimitsCardLabels,
  CuiWorkspaceValidationMessages,
} from "@coronet/cui";
import { i18n } from "@/plugins/i18n";
import { WorkspaceLocale } from "@/constants/workplaces";

// Over-capacity allowance is entered as a whole percentage in this range (PM decision, PLAT-3872).
const OVER_CAPACITY_MIN_PERCENT = 0;
const OVER_CAPACITY_MAX_PERCENT = 100;

export interface WorkspaceFormText {
  validationMessages: ComputedRef<CuiWorkspaceValidationMessages>;
  billingLabels: ComputedRef<CuiBillingFieldsLabels>;
  limitsLabels: ComputedRef<CuiWorkspaceLimitsCardLabels>;
  brandingLabels: ComputedRef<CuiBrandingFieldsLabels>;
  logosLabels: ComputedRef<CuiBrandingLogosLabels>;
  localeSelectLabels: ComputedRef<CuiLocaleSelectLabels>;
  localeDisplayNames: ComputedRef<Record<WorkspaceLocale, string>>;
}

/**
 * Text for the shared @coronet/cui workspace form components (cui takes no i18n), built from the
 * console's existing translation keys. Everything is computed, so a runtime locale switch updates
 * labels and validation messages alike.
 */
export function useWorkspaceFormText(): WorkspaceFormText {
  const validationMessages = computed<CuiWorkspaceValidationMessages>(() => ({
    required: i18n.global.t("validations.required"),
    email: i18n.global.t("validations.email"),
    domain: i18n.global.t("validations.domain"),
    domainZoneIsNotAllowed: i18n.global.t("validations.domainZoneIsNotAllowed"),
    invalidHexColor: i18n.global.t("validations.invalidHexColor"),
    invalidSubdomain: i18n.global.t("validations.invalidSubdomain"),
    noSpacesAllowed: i18n.global.t("validations.noSpacesAllowed"),
    maxNCharacters: (n) => i18n.global.t("validations.maxNCharacters", { n }),
  }));

  const billingLabels = computed<CuiBillingFieldsLabels>(() => ({
    title: i18n.global.t("modals.createWorkspace.billing"),
    netSuiteId: i18n.global.t("modals.editWorkspace.netSuiteId"),
    netSuiteLegacySubscriptionId: i18n.global.t(
      "modals.editWorkspace.netSuiteLegacySubscriptionId"
    ),
    newNetSuiteSubscriptionId: i18n.global.t("modals.editWorkspace.newNetSuiteSubscriptionId"),
    netSuiteSubscriptionId: i18n.global.t("modals.editWorkspace.netSuiteSubscriptionId"),
    nfrWorkspace: i18n.global.t("modals.editWorkspace.nfrWorkspace"),
    acvAccount: i18n.global.t("modals.editWorkspace.acvAccount"),
    rampProgram: i18n.global.t("modals.editWorkspace.rampProgram"),
    minimumCommitment: i18n.global.t("modals.editWorkspace.minimumCommitment"),
    onlyDigitsValidation: i18n.global.t("validations.onlyDigits"),
    rampRequiresNetSuiteIdsValidation: i18n.global.t("validations.rampRequiresNetSuiteIds"),
    requiredValidation: i18n.global.t("validations.required"),
    onlyIntegerValidation: i18n.global.t("validations.onlyInteger"),
  }));

  const limitsLabels = computed<CuiWorkspaceLimitsCardLabels>(() => ({
    unlimited: i18n.global.t("modals.editWorkspace.userDeviceLimits.unlimited"),
    limited: i18n.global.t("modals.editWorkspace.userDeviceLimits.limited"),
    limitedHelperText: i18n.global.t("modals.editWorkspace.userDeviceLimits.limitedHelperText"),
    numberOfProtectedUsers: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.numberOfProtectedUsers"
    ),
    numberOfProtectedDevices: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.numberOfProtectedDevices"
    ),
    numberOfProtectedDevicesHint: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.numberOfProtectedDevicesHint"
    ),
    overCapacityAllowance: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.overCapacityAllowance"
    ),
    overCapacityAllowanceHint: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.overCapacityAllowanceHint"
    ),
    summaryTitle: i18n.global.t("modals.editWorkspace.userDeviceLimits.summaryTitle"),
    totalMaxUsers: (total) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.totalMaxUsers", { n: total }),
    totalMaxDevices: (total) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.totalMaxDevices", { n: total }),
    // Spread: vue-i18n wants an index-signature object, and the cui params are interfaces.
    protectedUsersValidationWithCap: (params) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.protectedUsersValidationWithCap", {
        ...params,
      }),
    protectedUsersValidationWithoutCap: (params) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.protectedUsersValidationWithoutCap", {
        ...params,
      }),
    requiredValidation: i18n.global.t("validations.required"),
    onlyIntegerValidation: i18n.global.t("validations.onlyInteger"),
    minOverCapacityAllowanceValidation: i18n.global.t("validations.minValueAllowed", {
      n: OVER_CAPACITY_MIN_PERCENT,
    }),
    maxOverCapacityAllowanceValidation: i18n.global.t("validations.maxValueAllowed", {
      n: OVER_CAPACITY_MAX_PERCENT,
    }),
  }));

  const brandingLabels = computed<CuiBrandingFieldsLabels>(() => ({
    brandColorLabel: i18n.global.t("modals.editWorkspace.branding.brandColorLabel"),
    brandColorHint: i18n.global.t("modals.editWorkspace.branding.brandColorHint"),
    aliasLabel: i18n.global.t("modals.editWorkspace.branding.aliasLabel"),
    aliasHint: i18n.global.t("modals.editWorkspace.branding.aliasHint"),
    aliasFullDomainHint: (alias) =>
      i18n.global.t("modals.editWorkspace.branding.aliasFullDomainHint", { domain: alias }),
    noReplyEmailLabel: i18n.global.t("modals.editWorkspace.branding.noReplyEmailLabel"),
    noReplyEmailHint: i18n.global.t("modals.editWorkspace.branding.noReplyEmailHint"),
    supportEmailLabel: i18n.global.t("modals.editWorkspace.branding.supportEmailLabel"),
    supportEmailHint: i18n.global.t("modals.editWorkspace.branding.supportEmailHint"),
    companyAddressLabel: i18n.global.t("modals.editWorkspace.branding.companyAddressLabel"),
    companyAddressHint: i18n.global.t("modals.editWorkspace.branding.companyAddressHint"),
  }));

  const logosLabels = computed<CuiBrandingLogosLabels>(() => ({
    imageHint: i18n.global.t("logoUploader.imageHint"),
    imageDescription: i18n.global.t("logoUploader.description"),
    emailImageHint: i18n.global.t("logoUploader.emailImageHint"),
    emailImageDescription: i18n.global.t("logoUploader.emailDescription"),
    replaceImage: i18n.global.t("logoUploader.replaceImage"),
    imageError: i18n.global.t("logoUploader.imageError"),
  }));

  const localeSelectLabels = computed<CuiLocaleSelectLabels>(() => ({
    label: i18n.global.t("modals.createWorkspace.language.label"),
    placeholder: i18n.global.t("modals.createWorkspace.language.placeholder"),
    requiredValidation: i18n.global.t("validations.required"),
  }));

  const localeDisplayNames = computed((): Record<WorkspaceLocale, string> => {
    return Object.values(WorkspaceLocale).reduce(
      (acc, locale) => {
        acc[locale] = i18n.global.t(`general.localeDisplayNames.${locale}`);
        return acc;
      },
      {} as Record<WorkspaceLocale, string>
    );
  });

  return {
    validationMessages,
    billingLabels,
    limitsLabels,
    brandingLabels,
    logosLabels,
    localeSelectLabels,
    localeDisplayNames,
  };
}
